import type { FormNode } from '../../designer/types/form.types'

/** Entity dialog actions for runtime schema buttons (CommandBar / Button). */
export interface RuntimeFormDialogActions {
  submit: () => void
  save?: () => void
  close: () => void
  disabled?: boolean
}

function norm(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
    .toLowerCase()
}

/**
 * Mapping standard 1C-style button names/titles to submit / close.
 * Unknown buttons without mapping remain without action (disabled at runtime).
 */
export function resolveRuntimeFormButtonAction(
  node: Pick<FormNode, 'name' | 'title'>
): 'submit' | 'save' | 'close' | undefined {
  const key = norm(node.name || '')
  const tit = norm(node.title || '')

  const closeNames = [
    'close',
    'cancel',
    'exit',
    'abort',
    'annuler',
    'fermer',
  ]
  if (closeNames.includes(key)) return 'close'

  // "SaveAndClose" → submit (save + close form)
  const submitNames = [
    'saveandclose',
    'ok',
    'post',
    'enregistreretfermer',
  ]
  if (submitNames.includes(key)) return 'submit'

  // "Save" / "Write" → save only (stay on form)
  const saveOnlyNames = [
    'save',
    'write',
    'enregistrer',
  ]
  if (saveOnlyNames.includes(key)) return 'save'
  if (key.startsWith('saveand')) return 'submit'
  if (key.startsWith('save')) return 'save'

  const closeTitles = ['cancel', 'close', 'annuler', 'fermer']
  if (closeTitles.includes(tit)) return 'close'

  const submitTitles = ['ok', 'saveandclose', 'enregistreretfermer']
  if (submitTitles.includes(tit)) return 'submit'

  const saveOnlyTitles = ['save', 'write', 'enregistrer'] // localized save-button titles (en/fr)
  if (saveOnlyTitles.includes(tit)) return 'save'

  return undefined
}
